import { AuthenticationManager } from '@frontend/authentication-v2';
import { CheckboxInput } from '@frontend/components/basic-forms';
import { ChevronRight } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { useBrandingRepository } from '@frontend/repository';
import { IUser } from '@frontend/user/types';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { default as defaultLogo } from '../../../assets/images/nivaliz_logo_horizontal.png';
import ProfileAction from '../../profile/profile-actions/profile-action/profile-action.component';
import useAcceptEula from './accept-eula.controller';

const ID = 'accept-eula';
export interface AcceptEulaProps {
    user: IUser;
    callback?: () => void;
    overrideUrl?: string | null;
}

const AcceptEula = (props: AcceptEulaProps) => {
    const viewProps = useAcceptEula(props);
    const { logo_navbar_expanded } = useBrandingRepository();

    const logo = useMemo(() => {
        if (logo_navbar_expanded) return logo_navbar_expanded;
        return defaultLogo;
    }, [logo_navbar_expanded]);

    return (
        <div
            id={ID}
            className='flex h-dvh flex-col justify-start'>
            <div className='inline-flex h-48 flex-col items-center justify-center gap-2.5 self-stretch'>
                <img
                    className='img-fluid w-40'
                    src={logo}
                />
            </div>

            <div className='bg-background-default border-border-default inline-flex h-full flex-col items-center justify-between gap-12 self-stretch border-t px-5 py-6'>
                <div className='inline-flex flex-col items-start justify-start gap-8 self-stretch'>
                    <div className='flex flex-col justify-start gap-2'>
                        <div className='text-always-black justify-start text-xl font-semibold'>
                            <FormattedMessage
                                id='eula.user-agreement'
                                defaultMessage='User agreement'
                            />
                        </div>
                        <p className='text-always-black justify-start text-base font-medium'>
                            <FormattedMessage
                                id='eula.welcome-message'
                                defaultMessage='Welcome! Before you start using the app, please take a moment to review our End User License Agreement (EULA).'
                            />
                        </p>
                        <p className='text-always-black justify-start text-base font-medium'>
                            <FormattedMessage
                                id='eula.outline-message'
                                defaultMessage='It outlines your rights, responsibilities, and how we keep things running smoothly.'
                            />
                        </p>
                    </div>

                    <div className='flex flex-col items-start justify-start gap-6 self-stretch'>
                        <div className='flex w-full flex-col items-start justify-start gap-4'>
                            <ProfileAction
                                title={
                                    <FormattedMessage
                                        defaultMessage='End User License Agreement'
                                        id='eula.end-user-license-agreement'
                                    />
                                }
                                onClick={viewProps.onClickEulaLink}
                                icon={<ChevronRight className='text-brand-muted size-5' />}
                            />

                            <CheckboxInput
                                id='accept-eula-checkbox'
                                label={
                                    <FormattedMessage
                                        id='eula.accept-terms'
                                        defaultMessage='I have read and agree to the End User License Agreement.'
                                    />
                                }
                                value={viewProps.accepted}
                                onChange={(e) => viewProps.setAccepted(e)}
                            />
                        </div>
                    </div>
                </div>

                <div className='flex flex-col items-start justify-start gap-1 self-stretch'>
                    <button
                        id='accept-eula-confirm-button'
                        type='submit'
                        disabled={!viewProps.accepted}
                        onClick={viewProps.onSubmit}
                        className='bg-brand-primary disabled:bg-brand-primary/70 inline-flex h-12 items-center justify-center self-stretch rounded-xl p-4'>
                        <div className='text-background-surface justify-start text-base font-medium'>{CommonMessage.BUTTONS.CONTINUE}</div>
                    </button>
                    <button
                        onClick={() => {
                            AuthenticationManager.getInstance().logout(() => {
                                window.location.reload();
                            });
                        }}
                        id='cancel-eula-confirm-button'
                        className='inline-flex h-12 w-full items-center justify-center self-stretch rounded-xl p-4'>
                        <div className='text-brand-primary justify-start text-base font-medium'>{CommonMessage.BUTTONS.GO_BACK}</div>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AcceptEula;
